import React, { useEffect, useMemo, createContext, useState, useContext, useRef } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Spin, Form, Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Device from 'src/layouts/device';
import { localeSelector } from 'src/selector/app';
import { devicesSelector, isDeletingDeviceSelector } from 'src/selector/device';
import { modalMethods } from 'src/components/POSModal';
import { AuthorizedDropdownMenu } from 'src/components/Authorized';
import ScrollTable from 'src/components/ScrollTable';
import {
  doActivateDevice,
  doDeactivateDevice,
  doDeleteDevice,
  doUpdateDevice,
  fetchAllDevices,
} from 'src/actions/device';
import { DEVICE_ACTIVE, DEVICE_DEACTIVE, DEVICE_DELETE, MENU_PRINTER_MAPPING } from 'src/consts/permissionCodes';
import { DEVICE_TYPES, purposesToEnableConfigPrinterSetting, DEVICE_STATUS_ENUMS, DEVICE_TYPE_ENUMS } from 'src/consts';
import { formatTimeInSpecificZone, getItemName } from 'src/utils/utils';
import { PageMenuManagementPrinterMapping } from 'src/consts/routes';
import styles from './index.less';
import { withPermissionCheck } from 'src/components/HOC';

const EditableContext = createContext();
const ROW_KEY = 'id';
const ACTION_ACTIVATE = 'activate';
const ACTION_DEACTIVATE = 'deactivate';
const ACTION_DELETE = 'delete';
const ACTION_PRINTER_MAPPING = 'printerSetting';

const EditableCell = ({ editable, dataIndex, record = {}, children, onSave, ...restProps }) => {
  const { formatMessage } = useIntl();
  const [editing, setEditing] = useState(false);
  const form = useContext(EditableContext);
  const inputRef = useRef();

  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleEdit = () => {
    setEditing(true);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const handleSave = () => {
    setEditing(false);

    if (!form.getFieldValue(dataIndex)?.trim()) return;

    if (form.getFieldValue(dataIndex) !== record[dataIndex]) {
      onSave(record, ({ success }) => {
        if (!success) {
          form.setFieldsValue({ [dataIndex]: record[dataIndex] });
        }
      });
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
        rules={[
          {
            required: true,
            message: formatMessage({ id: `devices.column.${dataIndex}.required` }),
          },
        ]}
      >
        <Input ref={inputRef} onBlur={handleSave} onPressEnter={handleSave} />
      </Form.Item>
    ) : (
      <div onClick={handleEdit} className="editable-cell-value-wrap">
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const DeviceActionMenu = ({ record, onMenuClick }) => {
  const { device_type, printer_settings = [] } = record;
  const canEdit =
    device_type === DEVICE_TYPE_ENUMS.PRINTER &&
    printer_settings.find((_) => purposesToEnableConfigPrinterSetting.includes(_.purpose));
  const menuItems = [];
  if (record.status === DEVICE_STATUS_ENUMS.ACTIVE) {
    menuItems.push({ label: <FormattedMessage id="action.deactivate" />, key: ACTION_DEACTIVATE });
  } else {
    menuItems.push({ label: <FormattedMessage id="action.activate" />, key: ACTION_ACTIVATE });
  }
  menuItems.push({ label: <FormattedMessage id="action.delete" />, key: ACTION_DELETE });
  if (canEdit) {
    menuItems.push({ label: <FormattedMessage id="devices.printerSetting" />, key: ACTION_PRINTER_MAPPING });
  }

  return (
    <AuthorizedDropdownMenu
      record={record}
      onClick={onMenuClick}
      menuItemPermissionMap={{
        [ACTION_ACTIVATE]: DEVICE_ACTIVE,
        [ACTION_DEACTIVATE]: DEVICE_DEACTIVE,
        [ACTION_DELETE]: DEVICE_DELETE,
        [ACTION_PRINTER_MAPPING]: MENU_PRINTER_MAPPING,
      }}
      items={menuItems}
    />
  );
};

const AllDevices = () => {
  const locale = useSelector(localeSelector);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { isLoading, devices } = useSelector(devicesSelector);
  const isDeleting = useSelector(isDeletingDeviceSelector);
  const router = useRouter();
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchAllDevices());
  }, []);

  const handleDelete = (deviceId) => {
    modalMethods
      .confirm({
        messageId: 'devices.deleteDevice.promptContent',
      })
      .then(() => {
        dispatch(doDeleteDevice(deviceId));
      });
  };

  const handleActivate = (deviceId) => {
    modalMethods
      .confirm({
        messageId: 'devices.activateDevice.promptContent',
      })
      .then(() => {
        dispatch(
          doActivateDevice(deviceId, () => {
            dispatch(fetchAllDevices());
          })
        );
      });
  };

  const handleDeactivate = (deviceId) => {
    modalMethods
      .confirm({
        messageId: 'devices.deactivateDevice.promptContent',
      })
      .then(() => {
        dispatch(
          doDeactivateDevice(deviceId, () => {
            dispatch(fetchAllDevices());
          })
        );
      });
  };

  const handlePrintSetting = (deviceId) => {
    router.push({
      pathname: PageMenuManagementPrinterMapping,
      query: {
        deviceId,
      },
    });
  };

  const handleSave = async (record, callback) => {
    let hasErrors = false;
    let values;

    try {
      values = await form.validateFields();
    } catch (e) {
      hasErrors = true;
    }

    if (hasErrors) return;

    const params = {
      ...record,
      ...values,
    };
    dispatch(doUpdateDevice(params, callback));
  };

  const handleMenuClick = (key, record) => {
    switch (key) {
      case ACTION_ACTIVATE: {
        handleActivate(record.id);
        break;
      }

      case ACTION_DEACTIVATE: {
        handleDeactivate(record.id);
        break;
      }

      case ACTION_DELETE: {
        handleDelete(record.id);
        break;
      }

      case ACTION_PRINTER_MAPPING: {
        handlePrintSetting(record.id);
        break;
      }

      default: {
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: formatMessage({ id: 'devices.column.name' }),
        dataIndex: 'name',
        editable: true,
        width: 320,
        fixed: 'left',
        onCell: (record) => ({
          record,
          editable: true,
          dataIndex: 'name',
          title: formatMessage({ id: 'devices.column.name' }),
          onSave: handleSave,
        }),
      },
      {
        title: formatMessage({ id: 'devices.column.modelName' }),
        dataIndex: 'model_name',
        width: 200,
      },
      {
        title: formatMessage({ id: 'devices.column.type' }),
        dataIndex: 'device_type',
        width: 140,
        filters: Object.keys(DEVICE_TYPES).map((key) => ({ text: getItemName(DEVICE_TYPES[key], locale), value: key })),
        onFilter: (value, record) => record.device_type === value,
        render: (type) => getItemName(DEVICE_TYPES[type] || {}, locale),
      },
      {
        title: formatMessage({ id: 'devices.column.serialNumber' }),
        dataIndex: 'serial_number',
        width: 220,
      },
      {
        title: formatMessage({ id: 'devices.column.macAddress' }),
        dataIndex: 'mac_address',
        width: 180,
      },
      {
        title: formatMessage({ id: 'devices.column.status' }),
        dataIndex: 'status',
        width: 100,
        render: (status) => <FormattedMessage id={`device.status.${status}`} />,
      },
      {
        title: formatMessage({ id: 'devices.column.appVersion' }),
        dataIndex: 'app_version',
        width: 130,
      },
      {
        title: formatMessage({ id: 'devices.column.lastHeartBeatTime' }),
        dataIndex: 'last_heart_beat_received',
        width: 240,
        render: (time) => time > 0 && formatTimeInSpecificZone(time * 1000, { format: 'MM/DD/YYYY hh:mm:ss A' }),
      },
      {
        title: formatMessage({ id: 'devices.column.operation' }),
        key: 'action',
        width: 110,
        align: 'center',
        render: (_, record) => {
          const isKDS = record.device_type === DEVICE_TYPE_ENUMS.KITCHEN_DISPLAY_SYSTEM;
          return isKDS ? null : <DeviceActionMenu record={record} onMenuClick={handleMenuClick} />;
        },
      },
    ];
  }, [locale, handleMenuClick]);

  const tableProps = {
    columns,
    size: 'small',
    rowKey: ROW_KEY,
    dataSource: devices,
    loading: isLoading,
    pagination: false,
    components: { body: { cell: EditableCell } },
    fixHeight: `calc(100vh - 200px)`,
    bordered: true,
  };

  return (
    <Device>
      <div className={styles['device-page']}>
        <Spin spinning={isDeleting} tip={<FormattedMessage id="devices.isDeleting" />}>
          <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
              <ScrollTable {...tableProps} />
            </EditableContext.Provider>
          </Form>
        </Spin>
      </div>
    </Device>
  );
};

export default withPermissionCheck(AllDevices);
